import { Tag } from '@televet/kibble-ui/Tag';
import { Text } from '@televet/kibble-ui/Text';
import { TextInput } from '@televet/kibble-ui/TextInput';
import { Flex, Table } from '@televet/kibble-ui/chakra';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useCallback, useMemo } from 'react';
import { Breadcrumbs } from '@/components/Breadcrumbs';
import { PageContainer } from '@/components/PageContainer';
import { trpc } from '@/lib/trpc';
import { workflowStatusVariantMap } from '@/features/workflows/badgePalettes';
import { Label } from '@/features/workflows/components/messageBlock';
import { FilterMenu } from '@/features/listing-filters/FilterMenu';
import { FilterPills, type FilterPill } from '@/features/listing-filters/FilterPills';
import { buildGroupPills } from '@/features/listing-filters/groupPills';
import type { FilterGroup, FilterSelection } from '@/features/listing-filters/types';
import { useListingFilters } from '@/features/listing-filters/useListingFilters';
import { useDebouncedFilterText } from '@/features/listing-filters/useDebouncedFilterText';
import {
  WORKFLOW_STATUSES,
  DEFAULT_WORKFLOW_STATUSES,
  workflowsFiltersSchema,
  type WorkflowsFilterValues,
} from './-lib/filters';

const RouteComponent = () => {
  const navigate = useNavigate();
  const routeNavigate = Route.useNavigate();
  const filters = Route.useSearch();

  const [nameDraft, setNameDraft] = useDebouncedFilterText({
    value: filters.name,
    onCommit: (next) =>
      routeNavigate({ replace: true, search: (prev) => ({ ...prev, name: next }) }),
  });

  const applyFilters = useCallback(
    (next: WorkflowsFilterValues) => {
      routeNavigate({ search: next });
    },
    [routeNavigate]
  );

  useListingFilters({
    page: 'workflows',
    schema: workflowsFiltersSchema,
    filters,
    onRehydrate: applyFilters,
  });

  const { data } = useQuery(
    trpc.workflow.listWorkflows.queryOptions(
      useMemo(
        () => ({
          limit: 100,
          where: {
            ...(filters.name && { name: filters.name }),
            status: filters.status?.length ? filters.status : DEFAULT_WORKFLOW_STATUSES,
            ...(filters.runSchema?.length && { runSchema: filters.runSchema }),
          },
        }),
        [filters]
      ),
      { placeholderData: keepPreviousData }
    )
  );

  const workflows = data?.workflows;

  // Options come from their own query, not from the listing response: that
  // response is already filtered by the selected schema, so reading options off
  // it would collapse the list to the one value chosen and leave no way to add a
  // second. It is page-limited too, which would hide anything past the first
  // page.
  const { data: filterOptions } = useQuery(trpc.workflow.listWorkflowFilterOptions.queryOptions());

  const runSchemaOptions = useMemo(
    () => (filterOptions?.runSchemas ?? []).map((schema) => ({ label: schema, value: schema })),
    [filterOptions]
  );

  const groups = useMemo<FilterGroup[]>(
    () => [
      {
        key: 'status',
        label: 'Status',
        options: WORKFLOW_STATUSES.map((status) => ({ label: status, value: status })),
      },
      { key: 'runSchema', label: 'Run schema', options: runSchemaOptions },
    ],
    [runSchemaOptions]
  );

  const selection = useMemo<FilterSelection>(
    () => ({ status: filters.status ?? [], runSchema: filters.runSchema ?? [] }),
    [filters]
  );

  const handleSelectionChange = useCallback(
    (next: FilterSelection) => {
      applyFilters({
        ...filters,
        status: next.status as WorkflowsFilterValues['status'],
        runSchema: next.runSchema,
      });
    },
    [applyFilters, filters]
  );

  const pills = useMemo<FilterPill[]>(() => {
    const all = buildGroupPills({ groups, selection, onChange: handleSelectionChange });

    if (filters.name) {
      all.push({
        key: 'name',
        label: `Name: ${filters.name}`,
        onRemove: () => applyFilters({ ...filters, name: undefined }),
      });
    }
    return all;
  }, [groups, selection, handleSelectionChange, filters, applyFilters]);

  return (
    <PageContainer>
      <Flex direction="column" w="100%" gap={4}>
        <Breadcrumbs items={[{ label: 'Definitions' }]} />

        <Flex justify="space-between" align="center">
          <Text fontSize="lg" fontWeight="semibold" letterSpacing="-0.01em">
            Definitions
          </Text>
        </Flex>

        <Flex direction="column" gap={3}>
          <Flex gap={3} align="flex-end" wrap="wrap">
            <Flex direction="column" gap={1} width="260px" flexShrink={0}>
              <Label>Name</Label>
              <TextInput
                size="sm"
                placeholder="Search workflows..."
                value={nameDraft}
                onChange={(e) => setNameDraft(e.target.value)}
                data-testid="workflows-filter-name"
              />
            </Flex>
            <FilterMenu groups={groups} selection={selection} onChange={handleSelectionChange} />
          </Flex>
          <FilterPills
            pills={pills}
            onClearAll={() => {
              setNameDraft('');
              applyFilters({ name: undefined, status: [], runSchema: [] });
            }}
          />
        </Flex>
        <Table.Root size="sm" width="100%" variant="outline">
          <Table.Header>
            <Table.Row>
              <Table.ColumnHeader>Name</Table.ColumnHeader>
              <Table.ColumnHeader>Status</Table.ColumnHeader>
              <Table.ColumnHeader>Run Schema</Table.ColumnHeader>
              <Table.ColumnHeader textAlign="center">Steps</Table.ColumnHeader>
              <Table.ColumnHeader textAlign="center">Configs</Table.ColumnHeader>
              <Table.ColumnHeader textAlign="center">Runs</Table.ColumnHeader>
              <Table.ColumnHeader>Updated</Table.ColumnHeader>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {workflows?.map((workflow) => (
              <Table.Row
                key={workflow.id}
                cursor="pointer"
                _hover={{ bg: 'bg.muted' }}
                onClick={() =>
                  navigate({
                    to: '/workflows/$workflowId',
                    params: { workflowId: workflow.id },
                  })
                }
              >
                {/* Capped and truncated with plain CSS. Kibble's `truncated`
                    measures the rendered text and sets state, which in an
                    auto-layout table changes the column width and triggers
                    another measurement: the table visibly flickered on load. */}
                <Table.Cell maxW="360px">
                  <Flex direction="column">
                    <Text fontSize="sm" fontWeight="medium">
                      {workflow.name}
                    </Text>
                    {workflow.description && (
                      <Text
                        fontSize="xs"
                        variant="subtle"
                        whiteSpace="nowrap"
                        overflow="hidden"
                        textOverflow="ellipsis"
                        title={workflow.description}
                      >
                        {workflow.description}
                      </Text>
                    )}
                  </Flex>
                </Table.Cell>
                <Table.Cell>
                  <Tag
                    label={workflow.status}
                    variant={workflowStatusVariantMap[workflow.status] ?? 'default'}
                    size="sm"
                  />
                </Table.Cell>
                <Table.Cell>
                  <Text fontSize="sm" variant="subtle">
                    {workflow.runSchema ?? '-'}
                  </Text>
                </Table.Cell>
                <Table.Cell textAlign="center">{workflow._count.steps}</Table.Cell>
                <Table.Cell textAlign="center">{workflow._count.configurations}</Table.Cell>
                <Table.Cell textAlign="center">{workflow._count.runs}</Table.Cell>
                <Table.Cell>
                  <Text fontSize="sm" variant="subtle">
                    {new Date(workflow.updatedAt).toLocaleDateString()}
                  </Text>
                </Table.Cell>
              </Table.Row>
            ))}
          </Table.Body>
        </Table.Root>
        {workflows?.length === 0 && (
          <Flex justify="center" align="center" p={8}>
            <Text variant="subtle">No workflows found</Text>
          </Flex>
        )}
      </Flex>
    </PageContainer>
  );
};

export const Route = createFileRoute('/_authenticated/workflows/')({
  component: RouteComponent,
  validateSearch: workflowsFiltersSchema,
});
